import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TenantInfo {
  legalName: string;
  logoUrl: string | null;
  mision: string | null;
  vision: string | null;
  valores: string | null;
}

export default function LoginPage() {
  const { tenant } = useParams<{ tenant?: string }>();
  const tenantSlug = tenant;
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [rememberUser, setRememberUser] = useState(false);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const { login, isLoading } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (tenantSlug) {
      fetchTenantInfo();
      loadRememberedUser();
    }
  }, [tenantSlug]);

  const fetchTenantInfo = async () => {
    try {
      const response = await fetch(`/api/tenants/${tenantSlug}/public-info`);
      if (response.ok) {
        const data = await response.json();
        setTenantInfo(data);
      }
    } catch {
      // silently fail
    }
  };

  const loadRememberedUser = () => {
    try {
      const storageKey = `qcampus_remember_user_${tenantSlug}`;
      const rememberedData = localStorage.getItem(storageKey);
      if (rememberedData) {
        const { identifier: savedIdentifier } = JSON.parse(rememberedData);
        setIdentifier(savedIdentifier);
        setRememberUser(true);
      }
    } catch {}
  };

  const saveRememberedUser = (userIdentifier: string) => {
    try {
      const storageKey = `qcampus_remember_user_${tenantSlug}`;
      if (rememberUser) {
        localStorage.setItem(storageKey, JSON.stringify({ identifier: userIdentifier }));
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {}
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier || !password || !tenantSlug) return;
    setError('');
    try {
      await login(identifier, password, tenantSlug);
      saveRememberedUser(identifier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const hasCompanyContent = tenantInfo && (tenantInfo.mision || tenantInfo.vision || tenantInfo.valores);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* LEFT — Light minimalist panel */}
      <div className="relative hidden lg:flex lg:w-5/12 flex-col justify-between overflow-hidden bg-slate-50 p-14">
        {/* Top: Institution name */}
        <div className="text-center">
          {tenantInfo?.legalName ? (
            <p className="text-lg font-bold tracking-wide text-slate-700">{tenantInfo.legalName}</p>
          ) : (
            <p className="text-lg font-bold tracking-wide text-slate-700">Sistema de Control Escolar</p>
          )}
        </div>

        {/* Center: Content */}
        <div className="flex-1 flex flex-col justify-center py-10">
          {hasCompanyContent ? (
            <div className="space-y-4">
              {tenantInfo?.mision && (
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="mb-3">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <p className="font-bold text-slate-800 text-sm mb-1.5">Misión</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{tenantInfo.mision}</p>
                </div>
              )}
              {tenantInfo?.vision && (
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="mb-3">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <p className="font-bold text-slate-800 text-sm mb-1.5">Visión</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{tenantInfo.vision}</p>
                </div>
              )}
              {tenantInfo?.valores && (
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="mb-3">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <p className="font-bold text-slate-800 text-sm mb-1.5">Valores</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{tenantInfo.valores}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {[
                { title: 'Control Académico Completo', desc: 'Gestiona periodos, grupos, horarios y calificaciones en un solo lugar.', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
                { title: 'Finanzas y Facturación CFDI', desc: 'Contabilidad de doble entrada y emisión de CFDI 4.0.', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
                { title: 'Gestión de Alumnos 360°', desc: 'Portal de autoservicio con matrículas múltiples y seguimiento académico.', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="mb-3">
                    <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={item.icon} />
                    </svg>
                  </div>
                  <p className="font-bold text-slate-800 text-sm mb-1.5">{item.title}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom: minimal label */}
        <div>
          <p className="text-[10px] tracking-widest uppercase text-slate-300">QCampusOne</p>
        </div>
      </div>

      {/* RIGHT — Login form */}
      <div className="flex-1 flex items-center justify-center bg-white dark:bg-gray-900 p-6 lg:p-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex lg:hidden justify-center mb-8">
            <img
              src={tenantInfo?.logoUrl || '/qcampusone-logo.png'}
              alt={tenantInfo?.legalName || 'QCampusOne'}
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            {tenantInfo?.logoUrl && (
              <div className="hidden lg:flex mb-6">
                <img src={tenantInfo.logoUrl} alt={tenantInfo.legalName} className="h-14 w-auto object-contain rounded-lg shadow-sm" />
              </div>
            )}
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Bienvenido</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {tenantInfo?.legalName ? (
                <>Inicia sesión en <span className="font-semibold text-blue-600 dark:text-blue-400">{tenantInfo.legalName}</span></>
              ) : (
                <>Inicia sesión en <span className="font-semibold text-blue-600 dark:text-blue-400">{tenantSlug}</span></>
              )}
            </p>
          </div>

          {/* Error */}
          {error && (
            <Alert variant="destructive" className="mb-5">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                NUI o Correo electrónico
              </label>
              <Input
                type="text"
                placeholder="usuario@escuela.edu o NUI"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                required
                className="h-11"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Contraseña
              </label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="h-11"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  id="rememberUser"
                  checked={rememberUser}
                  onChange={(e) => setRememberUser(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">Recordar usuario</span>
              </label>
              <Link
                to={`/${tenantSlug}/recuperar-contrasena`}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button type="submit" className="w-full h-11 text-base font-semibold mt-2" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Iniciando sesión...
                </span>
              ) : 'Iniciar sesión'}
            </Button>
          </form>

          {/* Footer link */}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-500 hover:text-blue-600 dark:text-gray-400">
              ¿Otra institución? Cambiar empresa
            </Link>
          </div>

          {/* Demo credentials */}
          {tenant === 'testcampus' && (
            <div className="mt-5 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-800 dark:text-green-200 text-center">
                <strong>Demo:</strong> admin@testcampus.com / admin123456
              </p>
            </div>
          )}

          <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-600">
            Powered by <span className="font-semibold">QCampusOne</span> &mdash; Control Escolar Inteligente
          </p>
        </div>
      </div>
    </div>
  );
}
